// Unified organization sign-in via Google Cloud Identity Platform (project "iskcon-ciam").
// The SDK signs the user in with ANY enabled provider (Google, Facebook, Apple, Microsoft,
// …), and the resulting GCIP ID token is exchanged at /signin/apiAuthGcip for our own JWT.
// Configure via env:
//   VITE_GCIP_API_KEY     — the Identity Platform web API key (public identifier)
//   VITE_GCIP_AUTH_DOMAIN — e.g. iskcon-ciam.firebaseapp.com
//   VITE_GCIP_PROVIDERS   — comma list of provider ids to offer (default "google.com");
//                           a provider must ALSO be enabled in the GCIP console.
// Until VITE_GCIP_* is set, the login page falls back to the legacy GIS Google button.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  type AuthProvider,
} from "firebase/auth";

import { exchangeGcipIdToken, type LoginResult } from "@/lib/api";

const API_KEY = import.meta.env.VITE_GCIP_API_KEY as string | undefined;
const AUTH_DOMAIN = import.meta.env.VITE_GCIP_AUTH_DOMAIN as string | undefined;

export const GCIP_CONFIGURED = Boolean(API_KEY && AUTH_DOMAIN);

const PROVIDER_LABELS: Record<string, string> = {
  "google.com": "Google",
  "facebook.com": "Facebook",
  "apple.com": "Apple",
  "microsoft.com": "Microsoft",
};

/** The providers this build offers, in configured order. */
export const GCIP_PROVIDERS: { id: string; label: string }[] = (
  (import.meta.env.VITE_GCIP_PROVIDERS as string | undefined) ?? "google.com"
)
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean)
  .map((id) => ({ id, label: PROVIDER_LABELS[id] ?? id }));

let app: FirebaseApp | null = null;
function auth() {
  if (!app) {
    app = initializeApp({ apiKey: API_KEY, authDomain: AUTH_DOMAIN });
  }
  return getAuth(app);
}

function providerFor(id: string): AuthProvider {
  if (id === "google.com") {
    const p = new GoogleAuthProvider();
    p.setCustomParameters({ prompt: "select_account" });
    return p;
  }
  if (id === "facebook.com") {
    return new FacebookAuthProvider();
  }
  const p = new OAuthProvider(id);
  if (id === "apple.com") {
    p.addScope("email");
    p.addScope("name");
  }
  return p;
}

/** Thrown-away marker for "the user just closed the popup" — callers stay silent on it. */
export class SignInCancelled extends Error {}

/** Sign in with one GCIP provider and exchange the token for our app session. */
export async function signInWithGcipProvider(providerId: string): Promise<LoginResult> {
  let cred;
  try {
    cred = await signInWithPopup(auth(), providerFor(providerId));
  } catch (e) {
    const code = (e as { code?: string } | undefined)?.code ?? "";
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      throw new SignInCancelled();
    }
    if (code === "auth/operation-not-allowed") {
      throw new Error(
        `${PROVIDER_LABELS[providerId] ?? providerId} sign-in is not enabled yet. Contact the administrator.`
      );
    }
    if (code === "auth/account-exists-with-different-credential") {
      throw new Error("This email already signed in with a different provider. Use that provider instead.");
    }
    throw e instanceof Error ? e : new Error("Sign-in failed.");
  }
  const idToken = await cred.user.getIdToken();
  return exchangeGcipIdToken(idToken);
}
